/**
 * UnsavedChangesDialog — three-way resolution modal for `useUnsavedChangesGuard`.
 *
 * Unlike the stock ConfirmDialog (two buttons: Confirm / Cancel), this one
 * renders Save / Discard / Cancel because losing work has a clear "save me"
 * happy path distinct from "discard and move on". Matches the B15 plan
 * acceptance: "Save / Discard / Cancel — all three branches tested."
 *
 * Visual layout (mirrors GitHub, VSCode, and the web standard for dirty-nav
 * dialogs):
 *   ┌─────────────────────────────────────────────────┐
 *   │ Unsaved changes                                 │
 *   │                                                 │
 *   │ You have unsaved changes. What would you like   │
 *   │ to do?                                          │
 *   │                                                 │
 *   │             [ Cancel ]  [ Discard ]  [ Save ]   │
 *   └─────────────────────────────────────────────────┘
 *
 * - Save: primary action on the right (affirmative).
 * - Discard: destructive-tone (red/danger) in the middle.
 * - Cancel: ghost on the left (least commitment — "never mind, I'll stay").
 */

import React from 'react';
import { Modal } from './Modal/Modal';
import { Button } from './Button';
import { t } from '@/lib/i18n';

export interface UnsavedChangesDialogProps {
  isOpen: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  /** Override the default heading. Defaults to translation key "Unsaved changes". */
  title?: string;
  /** Override the body copy. Defaults to a generic prompt. */
  description?: string;
}

export function UnsavedChangesDialog({
  isOpen,
  onSave,
  onDiscard,
  onCancel,
  isSaving = false,
  title,
  description,
}: UnsavedChangesDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title ?? t('Unsaved changes')}
      size="sm"
      // Block Esc / backdrop dismissal while saving so the user can't abandon
      // a mid-flight save request by clicking outside the dialog.
      closeOnOverlayClick={!isSaving}
      closeOnEscape={!isSaving}
      footer={
        <div
          className="flex items-center justify-end gap-2 w-full"
          data-testid="unsaved-changes-dialog-footer"
        >
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isSaving}
            data-testid="unsaved-changes-cancel"
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onDiscard}
            disabled={isSaving}
            data-testid="unsaved-changes-discard"
          >
            {t('Discard')}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            isLoading={isSaving}
            data-testid="unsaved-changes-save"
          >
            {t('Save')}
          </Button>
        </div>
      }
    >
      <p
        className="text-sm text-muted-foreground"
        data-testid="unsaved-changes-description"
      >
        {description ??
          t(
            'You have unsaved changes. Save them to keep your work, discard them to leave without saving, or cancel to stay on this page.',
          )}
      </p>
    </Modal>
  );
}
